/**
 * Next-run prediction for pipeline schedules.
 *
 * The orchestrator API returns `cron_expression` and `time_zone` but no
 * `next_run_at`, so "when does this fire next?" has to be answered here. That
 * is the whole reason the Schedules page could not show a next-run column.
 *
 * Everything below works in the *schedule's own* timezone and returns real UTC
 * instants, so occurrences from schedules in different zones are directly
 * comparable — which is what lets the timeline plot an 02:00 ET job and a
 * 23:00 PT job in the same column.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { parseCron, cronRunsOnDay, matchingValues, FIELD_RANGE } from './cronFields';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Upper bound on the day walk. `0 0 29 2 *` (leap day only) needs up to ~1461
 * days; the margin covers a start date just past a February 29.
 */
const MAX_DAYS = 1500;

/** Default number of occurrences returned by {@link getNextRuns}. */
const DEFAULT_COUNT = 5;

export interface NextRun {
  /** The real UTC instant the run fires at. */
  instant: Date;
  /**
   * True when the cron named a wall-clock time that does not exist in the
   * zone — the spring-forward gap. The instant is the first valid moment
   * after the gap, matching what Unix cron does; the flag lets the UI say so
   * rather than silently showing a time nobody configured.
   */
  dstShifted: boolean;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * A wall-clock time in an IANA zone, resolved to a UTC instant.
 *
 * Deliberately builds a *string* and hands that to `dayjs.tz`. Passing a Date
 * reinterprets an existing instant rather than parsing a wall clock, which is
 * the classic silent off-by-one-hour in this kind of code.
 */
function wallClockToInstant(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  zone: string
): NextRun | null {
  const wall = `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:00`;

  let m: dayjs.Dayjs;
  try {
    m = dayjs.tz(wall, zone);
  } catch {
    return null;
  }
  if (!m.isValid()) return null;

  // Round-trip back to the zone. If the formatted result differs from what we
  // asked for, the wall clock did not exist and dayjs normalised it forward.
  const dstShifted = m.tz(zone).format('YYYY-MM-DDTHH:mm:00') !== wall;
  return { instant: m.toDate(), dstShifted };
}

/** Year/month/day as they read on the wall in `zone` at `instant`. */
function zonedYmd(instant: Date, zone: string): { y: number; mo: number; d: number } {
  const z = dayjs(instant).tz(zone);
  return { y: z.year(), mo: z.month() + 1, d: z.date() };
}

/** True when `zone` is an IANA name this runtime knows. */
function isValidZone(zone: string): boolean {
  try {
    dayjs().tz(zone);
    return true;
  } catch {
    return false;
  }
}

interface WalkOptions {
  from: Date;
  /** Stop once this many occurrences are collected. */
  count: number;
  /** Stop once an occurrence lands at or after this instant. */
  until?: Date | undefined;
}

/**
 * Shared day-first walk behind every public entry point.
 *
 * Day-first, never minute-scan: the matching minutes and hours are enumerated
 * once, then each calendar day is tested with a single `cronRunsOnDay` call.
 * For the dominant `m h * * dow` shape that is one instant construction per
 * matching day.
 */
function walk(cronExpression: string, zone: string, opts: WalkOptions): NextRun[] | null {
  const parts = parseCron(cronExpression);
  if (!parts) return null;
  if (!isValidZone(zone)) return null;

  const minutes = matchingValues(parts.minute, FIELD_RANGE.minute.min, FIELD_RANGE.minute.max);
  const hours = matchingValues(parts.hour, FIELD_RANGE.hour.min, FIELD_RANGE.hour.max);
  if (minutes.length === 0 || hours.length === 0) return null;

  const out: NextRun[] = [];
  const fromMs = opts.from.getTime();
  const untilMs = opts.until?.getTime();

  const cursor = zonedYmd(opts.from, zone);
  // Plain local Date used purely as a carrier for year/month/day so we can
  // step days and read weekday. `cronRunsOnDay` reads host-local fields, so it
  // must NOT be handed the raw UTC instant — that shifts the weekday whenever
  // the host and the schedule zone straddle midnight.
  const probe = new Date(cursor.y, cursor.mo - 1, cursor.d);

  for (let dayIndex = 0; dayIndex < MAX_DAYS; dayIndex++) {
    if (cronRunsOnDay(parts, probe)) {
      const y = probe.getFullYear();
      const mo = probe.getMonth() + 1;
      const d = probe.getDate();

      for (const h of hours) {
        for (const mi of minutes) {
          const run = wallClockToInstant(y, mo, d, h, mi, zone);
          if (!run) continue;

          const ms = run.instant.getTime();
          if (ms <= fromMs) continue;
          if (untilMs !== undefined && ms >= untilMs) return out;

          out.push(run);
          if (out.length >= opts.count) return out;
        }
      }
    }
    probe.setDate(probe.getDate() + 1);
  }

  return out;
}

/**
 * The next `count` occurrences after `from` (default: now).
 * Returns null when the expression or timezone cannot be understood — callers
 * should fall back to showing the raw cron string rather than a wrong time.
 */
export function getNextRuns(
  cronExpression: string,
  timeZone: string,
  opts?: { count?: number; from?: Date }
): NextRun[] | null {
  return walk(cronExpression, timeZone, {
    from: opts?.from ?? new Date(),
    count: opts?.count ?? DEFAULT_COUNT
  });
}

/** The single next occurrence, or null if the schedule cannot be read. */
export function getNextRun(cronExpression: string, timeZone: string, from?: Date): NextRun | null {
  const runs = walk(cronExpression, timeZone, { from: from ?? new Date(), count: 1 });
  return runs?.[0] ?? null;
}

export interface RunWindow {
  runs: NextRun[];
  /** True when `cap` cut the list short — e.g. a per-minute cron. */
  truncated: boolean;
}

/**
 * Every occurrence in `[start, end)`, capped.
 *
 * The cap exists because `* * * * *` would otherwise yield 1440 markers and
 * render the timeline as a solid bar; the caller shows a summary pill instead.
 */
export function getRunsInWindow(
  cronExpression: string,
  timeZone: string,
  start: Date,
  end: Date,
  cap = 48
): RunWindow | null {
  // `walk` is exclusive of `from`, but a window should include an occurrence
  // sitting exactly on its start edge — step back 1ms so midnight runs count.
  const from = new Date(start.getTime() - 1);
  const runs = walk(cronExpression, timeZone, { from, count: cap + 1, until: end });
  if (!runs) return null;

  return runs.length > cap
    ? { runs: runs.slice(0, cap), truncated: true }
    : { runs, truncated: false };
}

/**
 * Sort key for ordering schedules by next run.
 *
 * Total and finite by construction: a comparator that sees null produces
 * `null - null === 0` and `NaN` for mixed pairs, which leaves `Array.sort`
 * order unspecified. Rows that cannot be scheduled sort last, with genuinely
 * broken expressions after merely-disabled ones.
 */
export function nextRunSortKey(
  schedule: { cron_expression: string; time_zone: string; is_enabled: boolean },
  now: Date
): number {
  const run = getNextRun(schedule.cron_expression, schedule.time_zone, now);
  if (!run) return Number.MAX_SAFE_INTEGER;
  if (!schedule.is_enabled) return Number.MAX_SAFE_INTEGER - 1;
  return run.instant.getTime();
}

/**
 * The timezone's short name *at that instant* — EST vs EDT, not a static label.
 * Falls back to the raw zone name if the runtime declines to produce one.
 */
export function tzAbbrev(instant: Date, zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short'
    }).formatToParts(instant);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? zone;
  } catch {
    return zone;
  }
}

/** Format an instant as it reads on the wall in `zone`. */
export function formatInZone(instant: Date, zone: string, fmt = 'ddd h:mm A'): string {
  try {
    return dayjs(instant).tz(zone).format(fmt);
  } catch {
    return dayjs(instant).format(fmt);
  }
}

/**
 * Short forward-looking relative time — `in 6h`, `in 12m`, `in 3d`.
 *
 * The mirror of `formatRelativeTime` in pipelineFormat.ts, which only looks
 * backwards. Kept to the same coarse granularity so "last run 3h ago / next
 * run in 6h" reads as one scale.
 */
export function formatTimeUntil(instant: Date, now: Date = new Date()): string {
  const diffSec = Math.round((instant.getTime() - now.getTime()) / 1000);
  if (diffSec <= 0) return 'now';
  if (diffSec < 60) return `in ${diffSec}s`;

  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `in ${mins}m`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    const remainMins = mins % 60;
    return remainMins > 0 ? `in ${hrs}h ${remainMins}m` : `in ${hrs}h`;
  }

  const days = Math.floor(hrs / 24);
  if (days < 30) return `in ${days}d`;

  return `in ${Math.floor(days / 30)}mo`;
}
